import { UndoRedoEntry } from '@/components/Recipe/undoRedo';
import { applyDraftToolOutputs } from '@/queries/useWorkflowApp';
import type {
  ToolConfigFileOutputExample,
  ToolConfigInput,
  ToolConfigOutput,
  ToolConfigOutputExample,
  ToolConfigStoredFile,
} from '@/types/tool-config.types';
import type { NodeId } from 'web';

/** What `FlowView.deleteNodes` will actually remove. Locked nodes and locked groups stay. */
export type ToolOutputDeleteGraph = {
  isLocked: (nodeId: NodeId) => boolean;
  isGroup: (nodeId: NodeId) => boolean;
  /** Direct children. `locked` is the child's own lock, the same check `deleteNodes` uses. */
  children: (groupId: NodeId) => readonly { id: NodeId; locked: boolean }[];
};

export type PlacedToolOutput = {
  output: ToolConfigOutput;
  index: number;
};

/**
 * Output entries this delete would drop. Group children count, because deleting the group deletes
 * them too. A locked node is not deleted, so its output stays.
 */
export function toolOutputsRemovedByDelete(
  requestedIds: readonly NodeId[],
  outputs: readonly ToolConfigOutput[],
  graph: ToolOutputDeleteGraph,
): ToolConfigOutput[] {
  if (outputs.length === 0 || requestedIds.length === 0) {
    return [];
  }

  const requested = new Set(requestedIds);
  const removedIds = new Set<NodeId>();
  for (const id of requestedIds) {
    if (graph.isLocked(id)) {
      continue;
    }
    if (!graph.isGroup(id)) {
      removedIds.add(id);
      continue;
    }
    const children = graph.children(id);
    if (children.some((child) => child.locked)) {
      continue;
    }
    removedIds.add(id);
    for (const child of children) {
      if (!requested.has(child.id)) {
        removedIds.add(child.id);
      }
    }
  }

  return outputs.filter((output) => removedIds.has(output.nodeId));
}

/**
 * Saved outputs whose every incoming edge is in this delete. An output with another incoming edge
 * stays eligible, and one that already has none is not newly unwired by this delete.
 */
export function toolOutputsUnwiredByEdgeDelete(
  edgeIds: readonly string[],
  edges: readonly { id: string; target: NodeId }[],
  outputs: readonly ToolConfigOutput[],
): ToolConfigOutput[] {
  if (outputs.length === 0 || edgeIds.length === 0) {
    return [];
  }
  const deleting = new Set(edgeIds);
  const incoming = new Map<NodeId, string[]>();
  for (const edge of edges) {
    const ids = incoming.get(edge.target);
    if (ids) {
      ids.push(edge.id);
    } else {
      incoming.set(edge.target, [edge.id]);
    }
  }
  return outputs.filter((output) => {
    const ids = incoming.get(output.nodeId);
    return ids !== undefined && ids.every((id) => deleting.has(id));
  });
}

function cloneStoredFile(file: ToolConfigStoredFile): ToolConfigStoredFile {
  return {
    ...file,
    ...(file.blobstore === undefined ? {} : { blobstore: { ...file.blobstore } }),
  };
}

function cloneFileExample(example: ToolConfigFileOutputExample): ToolConfigFileOutputExample {
  return { kind: 'file', type: example.type, file: cloneStoredFile(example.file) };
}

function cloneExample(example: ToolConfigOutputExample): ToolConfigOutputExample {
  if (example.kind === 'text') {
    return { kind: 'text', text: example.text };
  }
  return cloneFileExample(example);
}

/** Owned copy. The live draft row is replaced on delete, so undo cannot read examples off it. */
function cloneToolOutput(output: ToolConfigOutput): ToolConfigOutput {
  return {
    nodeId: output.nodeId,
    ...(output.examples === undefined ? {} : { examples: output.examples.map(cloneExample) }),
    ...(output.uploads === undefined ? {} : { uploads: output.uploads.map(cloneFileExample) }),
  };
}

/**
 * The same entries, with the indexes they occupy in the current draft list.
 * Each entry is a copy, including its examples, kept on the undo step until the node delete is reversed.
 */
export function placedToolOutputs(
  outputs: readonly ToolConfigOutput[],
  removed: readonly ToolConfigOutput[],
): PlacedToolOutput[] {
  const removedIds = new Set(removed.map((output) => output.nodeId));
  return outputs.flatMap((output, index) =>
    removedIds.has(output.nodeId) ? [{ output: cloneToolOutput(output), index }] : [],
  );
}

export function dropToolOutputs(outputs: ToolConfigOutput[], placed: readonly PlacedToolOutput[]): ToolConfigOutput[] {
  if (placed.length === 0) {
    return outputs;
  }
  const ids = new Set(placed.map((entry) => entry.output.nodeId));
  const next = outputs.filter((output) => !ids.has(output.nodeId));
  return next.length === outputs.length ? outputs : next;
}

/** Puts entries back at the indexes they had before the delete. Later edits around them stay. */
export function insertToolOutputs(
  outputs: ToolConfigOutput[],
  placed: readonly PlacedToolOutput[],
): ToolConfigOutput[] {
  if (placed.length === 0) {
    return outputs;
  }
  const savedById = new Map(placed.map((entry) => [entry.output.nodeId, entry.output]));
  let restoredExamples = false;
  const withExamples = outputs.map((output) => {
    const saved = savedById.get(output.nodeId);
    const savedSomething = (saved?.examples?.length ?? 0) > 0 || (saved?.uploads?.length ?? 0) > 0;
    const liveSomething = (output.examples?.length ?? 0) > 0 || (output.uploads?.length ?? 0) > 0;
    // The row can still be here with its examples and uploads already cleared. Put the saved ones back.
    if (!saved || !savedSomething || liveSomething) {
      return output;
    }
    restoredExamples = true;
    return saved;
  });
  const present = new Set(withExamples.map((output) => output.nodeId));
  const missing = placed.filter((entry) => !present.has(entry.output.nodeId)).sort((a, b) => a.index - b.index);
  if (!restoredExamples && missing.length === 0) {
    return outputs;
  }
  const next = withExamples.slice();
  for (const { output, index } of missing) {
    next.splice(Math.min(index, next.length), 0, output);
  }
  return next;
}

/** Puts back input rows an output-add removed, skipping any id the draft already has. */
function restoreToolInputs(inputs: ToolConfigInput[], restored: readonly ToolConfigInput[]): ToolConfigInput[] {
  if (restored.length === 0) {
    return inputs;
  }
  const present = new Set(inputs.map((input) => input.nodeId));
  const missing = restored.filter((input) => !present.has(input.nodeId));
  return missing.length === 0 ? inputs : [...inputs, ...missing];
}

function withoutToolInputs(inputs: ToolConfigInput[], removed: readonly ToolConfigInput[]): ToolConfigInput[] {
  if (removed.length === 0) {
    return inputs;
  }
  const ids = new Set(removed.map((input) => input.nodeId));
  const next = inputs.filter((input) => !ids.has(input.nodeId));
  return next.length === inputs.length ? inputs : next;
}

/**
 * Undo writes the copied outputs, examples included, back onto the draft. The returned entry is
 * the redo, which drops them again. The node restore is on the same undo entry, applied first.
 * `removedInputs` are rows the add cleared so the node was not stored as both; redo strips them again.
 */
export function toolOutputRestoreUndo(
  recipeId: string,
  placed: readonly PlacedToolOutput[],
  removedInputs: readonly ToolConfigInput[] = [],
): UndoRedoEntry {
  return UndoRedoEntry.fromCallback(() => {
    void applyDraftToolOutputs(
      recipeId,
      (outputs) => insertToolOutputs(outputs, placed),
      (inputs) => withoutToolInputs(inputs, removedInputs),
    );
    return toolOutputDropUndo(recipeId, placed, removedInputs);
  });
}

/**
 * Undo of a Tool-tab add (or redo of a remove): drops the placed outputs again.
 * `restoredInputs` are the stale input rows that add removed; undo puts them back.
 */
export function toolOutputDropUndo(
  recipeId: string,
  placed: readonly PlacedToolOutput[],
  restoredInputs: readonly ToolConfigInput[] = [],
): UndoRedoEntry {
  return UndoRedoEntry.fromCallback(() => {
    void applyDraftToolOutputs(
      recipeId,
      (outputs) => dropToolOutputs(outputs, placed),
      (inputs) => restoreToolInputs(inputs, restoredInputs),
    );
    return toolOutputRestoreUndo(recipeId, placed, restoredInputs);
  });
}
